/**
 * Public share links to documents (FW-35): a member shares one version of
 * a document through a link anyone can open without signing in, where the
 * organisation has turned it on (``documents.public_share_links``).
 *
 * The link itself (``/s/<token>``) comes back only on create; the lists
 * never carry it again.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export type ShareLinkView = {
  id: string;
  document_id: string;
  document_title: string;
  document_version: number;
  /** The sharer's display name (never an email); ``null``: say "a member". */
  created_by_name: string | null;
  created_at: string;
  /** ``null``: the link never expires. */
  expires_at: string | null;
  view_count: number;
  /** The viewer made it, or is an admin of its organisation. */
  can_revoke: boolean;
  /** Only on the create response: ``/s/<token>``, shown once. */
  path?: string | null;
};

export type DocumentShareLinks = {
  /** The platform and the document's organisation allow sharing now. */
  enabled: boolean;
  /** Lifetimes to pick from, in days; ``null`` never expires. */
  expiry_days: (number | null)[];
  default_expiry_days: number;
  /** Active links of every version of the document. */
  links: ShareLinkView[];
};

const documentShareLinksKey = (documentId: string) =>
  ["share-links", "document", documentId] as const;
export const orgShareLinksKey = (slug: string) => ["share-links", "org", slug] as const;

export function useDocumentShareLinks(documentId: string, enabled = true) {
  return useQuery({
    queryKey: documentShareLinksKey(documentId),
    queryFn: async () =>
      (await api.get<DocumentShareLinks>(`/documents/${documentId}/share-links`)).data,
    enabled: Boolean(documentId) && enabled,
  });
}

export function useCreateShareLink(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expiresInDays: number | null) =>
      (
        await api.post<ShareLinkView>(`/documents/${documentId}/share-links`, {
          expires_in_days: expiresInDays,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["share-links"] }),
  });
}

export function useRevokeShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/share-links/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["share-links"] }),
  });
}

/** Every active link of the organisation, for its admins. */
export function useOrgShareLinks(slug: string, enabled = true) {
  return useQuery({
    queryKey: orgShareLinksKey(slug),
    queryFn: async () => (await api.get<ShareLinkView[]>(`/orgs/${slug}/share-links`)).data,
    enabled: Boolean(slug) && enabled,
    retry: false,
  });
}

/** The full link on the origin the user is on (a deployment may answer
 *  on several domains). */
export function shareLinkUrl(path: string): string {
  return `${window.location.origin}${path}`;
}
